import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import works from "../../../apiData/works.json";
import { WorksPageMeta } from "./WorksSeoMeta";
import useWorksAnalytics from "./useWorksAnalytics";
import {
  Section, BgBlob, DotGrid, Inner,
  HeaderWrap, Eyebrow, SectionTitle, SectionDesc, Divider,
  StepsStrip, StepDot, StepLine,
  CardsGrid, Card, StepChip, QuoteMark,
  AvatarRing, AvatarInner, AvatarImg, AvatarFallback,
  CardTitle, CardText, Stars, CtaBtn,
  CtaBanner, BannerOrb, BannerTitle, BannerDesc, BannerBtn,
} from "./Works.styles";

// ─── Icon atoms ────────────────────────────────────────────────────────────────

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

// ─── Star rating ───────────────────────────────────────────────────────────────

const StarRating = ({ count = 5 }) => (
  <Stars aria-label={`${count} out of 5 stars`}>
    {Array.from({ length: 5 }, (_, i) => (
      <span key={i} aria-hidden="true">{i < count ? "★" : "☆"}</span>
    ))}
  </Stars>
);

// ─── Step → destination routing ────────────────────────────────────────────────
// Keyed by works.json's `id` field. Adjust the paths here if your actual
// routes differ — these are the only two lines that encode where each step
// sends the user.
//   Step 1 "Choose Your Program"  → the package/pricing page
//   Step 2 "Choose Your Schedule" → the new availability/schedule page
//   Anything else (e.g. step 3 "Meet Your Instructor", same as the banner)
//   falls back to the contact page, matching the original behavior.
const STEP_DESTINATIONS = {
  1: "/packages",
  2: "/availability",
  3: "/about"
};
const DEFAULT_DESTINATION = "/contactUs";

const getDestination = (item) => STEP_DESTINATIONS[item.id] || DEFAULT_DESTINATION;

// ─── WorkCard ──────────────────────────────────────────────────────────────────

const FALLBACK_EMOJIS = ["🧘", "🌿", "🪷", "☀️", "🌊", "✨"];

const WorkCard = ({ item, index, onCardClick, onCtaClick }) => {
  const [imgError, setImgError] = useState(false);

  return (
    <Card
      $index={index}
      onClick={onCardClick}
      role="button"
      tabIndex={0}
      aria-label={`${item.name} — click to continue`}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onCardClick(); }}
    >
      {/* Step number chip */}
      <StepChip>Step {index + 1}</StepChip>

      {/* Decorative quote mark */}
      {/* <QuoteMark aria-hidden="true">"</QuoteMark> */}

      {/* Floating avatar */}
      <AvatarRing $index={index} aria-hidden="true">
        <AvatarInner>
          {item.image && !imgError ? (
            <AvatarImg
              src={item.image}
              alt={item.name}
              onError={() => setImgError(true)}
            />
          ) : (
            <AvatarFallback>
              {FALLBACK_EMOJIS[index % FALLBACK_EMOJIS.length]}
            </AvatarFallback>
          )}
        </AvatarInner>
      </AvatarRing>

      {/* Star rating */}
      {/* <StarRating count={5} /> */}

      {/* Title — the "name" field acts as a step title */}
      <CardTitle>{item.name}</CardTitle>

      {/* Testimonial / description text */}
      <CardText>{item.comments}</CardText>

      {/* CTA */}
      <CtaBtn
        type="button"
        onClick={(e) => { e.stopPropagation(); onCtaClick(); }}
        aria-label={`${item.button} — ${item.name}`}
      >
        {item.button} <ArrowIcon />
      </CtaBtn>
    </Card>
  );
};

// ─── Works ────────────────────────────────────────────────────────────────────

const Works = () => {
  const navigate = useNavigate();
  const analytics = useWorksAnalytics();
  const sectionRef = useRef(null);

  // Fire 'works_section_view' once the section actually scrolls into view,
  // rather than on mount — it can sit well below the fold of its host page.
  useEffect(() => {
    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      analytics.trackSectionView();
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          analytics.trackSectionView();
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [analytics]);

  const handleStepClick = useCallback((item, source) => {
    const destination = getDestination(item);
    analytics.trackStepClick({
      stepId: item.id,
      stepName: item.name,
      destination,
      source,
    });
    navigate(destination, { state: { fromStep: item.id, fromStepName: item.name } });
  }, [navigate, analytics]);

  const handleBannerClick = useCallback(() => {
    analytics.trackBannerCtaClick(DEFAULT_DESTINATION);
    navigate(DEFAULT_DESTINATION);
  }, [navigate, analytics]);

  return (
    <Section id="testimonial" ref={sectionRef}>
      <WorksPageMeta steps={works} />

      {/* Background decoration */}
      <BgBlob className="b1" aria-hidden="true" />
      <BgBlob className="b2" aria-hidden="true" />
      <BgBlob className="b3" aria-hidden="true" />
      <DotGrid aria-hidden="true" />

      <Inner>
        {/* ── Header ── */}
        <HeaderWrap>
          <Eyebrow>✦ Our Process</Eyebrow>
          <SectionTitle>Your Yoga Journey Starts in 3 Simple Steps</SectionTitle>
          <Divider aria-hidden="true" />
          <SectionDesc>
            From choosing your program to joining your first live session, getting started with Yoga Shastra is simple.
          </SectionDesc>
        </HeaderWrap>

        {/* ── Step connector strip (tablet & desktop) ── */}
        <StepsStrip aria-hidden="true">
          {works?.map((_, i) => (
            <React.Fragment key={i}>
              <StepDot $active>{i + 1}</StepDot>
              {i < works.length - 1 && <StepLine />}
            </React.Fragment>
          ))}
        </StepsStrip>

        {/* ── Cards ── */}
        <CardsGrid>
          {works?.map((item, i) => (
            <WorkCard
              key={`${item.name}_${i}`}
              item={item}
              index={i}
              onCardClick={() => handleStepClick(item, "card")}
              onCtaClick={() => handleStepClick(item, "cta_button")}
            />
          ))}
        </CardsGrid>

        {/* ── Bottom CTA banner ── */}
        <CtaBanner>
          <BannerOrb className="o1" aria-hidden="true" />
          <BannerOrb className="o2" aria-hidden="true" />
          <BannerTitle>
  Ready to Start Your Practice?
          </BannerTitle>
          <BannerDesc>
            Take the first step toward a yoga practice designed around your
  goals, experience, and lifestyle. Start with a free trial and meet
  your instructor.
          </BannerDesc>
          <BannerBtn
            type="button"
            onClick={handleBannerClick}
            aria-label="Book your free yoga trial"
          >
            Book Your Free Trial <ArrowIcon />
          </BannerBtn>
        </CtaBanner>
      </Inner>
    </Section>
  );
};

Works.displayName = "Works";
export default React.memo(Works);